import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

// Re-links a desktop uploader install with fresh tokens by firing the
// chargestack-upload://auth deep link. Reuses the same protocol that the
// Activity Monitor's "Open Desktop Uploader" button uses — the uploader's
// main process picks up the URL, validates the JWT claims, swaps in fresh
// tokens, and emits auth-restored to resume any auth-paused uploads.
//
// Triggered from the desktop app's AuthBanner: shell.openExternal opens the
// user's default browser at this route.
export default function RelinkUploader() {
  const { user, token, loading, login, authProvider } = useAuth();
  const [step, setStep] = useState('idle');
  // 'idle' | 'firing' | 'fired' | 'need-login'

  useEffect(() => {
    if (loading) return;
    if (!user || !token) {
      setStep('need-login');
      return;
    }
    setStep('firing');
    fireDeepLink(token, user);
    setStep('fired');
  }, [loading, user, token]);

  if (loading || step === 'idle') {
    return <Centered>Preparing…</Centered>;
  }

  if (step === 'need-login') {
    return (
      <Centered>
        <h2 style={{ marginBottom: 8 }}>Sign in to relink the uploader</h2>
        <p style={{ color: '#475569', marginBottom: 24 }}>
          Sign in here, then your desktop uploader will receive fresh tokens automatically.
        </p>
        <button
          className="btn btn-primary btn-lg"
          onClick={async () => {
            if (authProvider === 'google_identity') {
              // The sign-in page finishes on Google and comes back to the page
              // stored here, which then fires the deep link with the new session.
              // (login() below is the Firebase pop-up; it cannot sign anyone in
              // under the Google sign-in — found 2026-10-09.)
              sessionStorage.setItem('dc_return_to', '/auth/re-link-uploader');
              window.location.href = '/login';
              return;
            }
            try { await login(); } catch (e) { console.error(e); }
          }}
        >
          Sign in
        </button>
      </Centered>
    );
  }

  return (
    <Centered>
      <h2 style={{ marginBottom: 8 }}>Uploader relinked</h2>
      <p style={{ color: '#475569', marginBottom: 4 }}>
        Fresh sign-in delivered to the Chargestack Uploader.
      </p>
      <p style={{ color: '#64748b', fontSize: 14, marginBottom: 24 }}>
        Paused uploads will resume automatically. You can close this tab.
      </p>
      <button className="btn btn-ghost" onClick={() => window.close()}>
        Close tab
      </button>
    </Centered>
  );
}

function fireDeepLink(token, user) {
  const refreshToken = localStorage.getItem('dc_refresh_token') || '';
  const params = new URLSearchParams({
    token,
    refresh_token: refreshToken,
    email: user.email || '',
    uid: user.uid || '',
    name: user.name || user.email || '',
  });
  const deepLink = `chargestack-upload://auth?${params.toString()}`;

  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.src = deepLink;
  document.body.appendChild(iframe);
  setTimeout(() => iframe.remove(), 800);
}

function Centered({ children }) {
  return (
    <div
      style={{
        minHeight: 'calc(100vh - 80px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        textAlign: 'center',
      }}
    >
      <div style={{ maxWidth: 460 }}>{children}</div>
    </div>
  );
}
